import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Typography,
  Box
} from "@material-ui/core";

import {
  Search as SearchIcon,
  Business as CustomerIcon,
  Place as SiteIcon,
  Person as AccountIcon,
  Work as JobIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from "@material-ui/icons";

import PageTitle from "../../components/PageTitle";
import ttAPI from "../../api/ttAPI";
import useStyles from "./styles";


const emptyResults = {
  customers: [],
  jobs: [],
  sites: [],
  accounts: []
};


export default function GlobalSearch(props) {

  const classes = useStyles();

  const queryParams = new URLSearchParams(props.location.search);
  const initialKey = queryParams.get("q") || "";

  const [searchKey, setSearchKey] = useState(initialKey);
  const [results, setResults] = useState(emptyResults);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [message, setMessage] = useState("");


  useEffect(() => {

    const key = new URLSearchParams(props.location.search).get("q") || "";

    setSearchKey(key);

    if (key.trim().length >= 3) {
      performSearch(key.trim());
    }

  }, [props.location.search]);


  const performSearch = async (key) => {

    if (key.length < 3) {
      setResults(emptyResults);
      setHasSearched(false);
      setMessage("Enter at least 3 characters to search.");
      return;
    }

    setIsLoading(true);
    setMessage("");

    const response = await ttAPI.globalSearch(key);

    setResults({
      customers: response && response.customers ? response.customers : [],
      jobs: response && response.jobs ? response.jobs : [],
      sites: response && response.sites ? response.sites : [],
      accounts: response && response.accounts ? response.accounts : []
    });

    setHasSearched(true);
    setIsLoading(false);
  };


  const handleSearch = event => {

    event.preventDefault();

    const key = searchKey.trim();

    if (key.length < 3) {
      setResults(emptyResults);
      setHasSearched(false);
      setMessage("Enter at least 3 characters to search.");
      return;
    }

    props.history.push(
      `/app/search?q=${encodeURIComponent(key)}`
    );
  };


  const goToCustomer = id => {
    window.open(`#/app/customer/edit/${id}`, "_blank");
  };

  const goToSite = id => {
    window.open(`#/app/site/edit/${id}`, "_blank");
  };

  const goToAccount = id => {
    window.open(`#/app/account/edit/${id}`, "_blank");
  };

  const goToJob = id => {
    window.open(`#/app/job/edit/${id}`, "_blank");
  };


  const getTotalResults = () => {
    return results.customers.length +
           results.jobs.length +
           results.sites.length +
           results.accounts.length;
  };


  const renderInactiveChip = item => {

    if (item.active !== false) {
      return null;
    }

    return (
      <Chip
        label="Inactive"
        size="small"
        className={classes.inactiveChip}
      />
    );
  };

  const highlightText = text => {

    if (!text || !searchKey.trim()) {
      return text;
    }

    const key = searchKey.trim();

    const parts = String(text).split(
      new RegExp(`(${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
    );

    return parts.map((part, index) =>
      part.toLowerCase() === key.toLowerCase()
        ? (
            <span key={index} className={classes.highlight}>
              {part}
            </span>
          )
        : part
    );
  };

  const highlightPhone = value => {
    if (!value || !searchKey.trim()) return value;

    const searchDigits = searchKey.replace(/\D/g, "");
    const phoneDigits = String(value).replace(/\D/g, "");

    if (
      searchDigits.length >= 6 &&
      phoneDigits.includes(searchDigits)
    ) {
      return (
        <span className={classes.highlight}>
          {value}
        </span>
      );
    }

    return value;
  };

  const highlightContact = value => {
    if (!value || !searchKey.trim()) return value;

    const searchDigits = searchKey.replace(/\D/g, "");
    const contactDigits = String(value).replace(/\D/g, "");

    if (
      searchDigits.length >= 6 &&
      contactDigits.includes(searchDigits)
    ) {
      return (
        <span className={classes.highlight}>
          {value}
        </span>
      );
    }

    return highlightText(value);
  };

  const getMatchSnippet = (text) => {

    if (!text || !searchKey.trim()) {
      return text;
    }

    const value = String(text);
    const key = searchKey.trim();

    const matchIndex = value
      .toLowerCase()
      .indexOf(key.toLowerCase());

    if (matchIndex === -1) {
      return value.length > 250
        ? value.substring(0, 250) + "..."
        : value;
    }

    let start = Math.max(0, matchIndex - 100);
    let end = Math.min(
      value.length,
      matchIndex + key.length + 150
    );

    // Avoid chopping a word in half
    if (start > 0) {
      const nextSpace = value.indexOf(" ", start);

      if (nextSpace !== -1 && nextSpace < matchIndex) {
        start = nextSpace + 1;
      }
    }

    if (end < value.length) {
      const nextSpace = value.indexOf(" ", end);

      if (nextSpace !== -1) {
        end = nextSpace;
      }
    }

    return (
      (start > 0 ? "..." : "") +
      value.substring(start, end) +
      (end < value.length ? "..." : "")
    );
  };


  const renderMatchedText = item => {

    if (!item.matched_text) {
      return null;
    }

    return (
      <div className={classes.matchedText}>
        <Typography
          variant="caption"
          className={classes.matchedLabel}
        >
          Matched in {item.matched_label || item.matched_field}
        </Typography>

        <Typography variant="body2">
          {highlightText(getMatchSnippet(item.matched_text))}
        </Typography>
      </div>
    );
  };


  return (
    <>

      <div className={classes.stickySearchArea}>
        <PageTitle title="Global Search" />
        <Paper className={classes.searchContainer}>

          <form
            onSubmit={handleSearch}
            className={classes.searchForm}
          >

            <TextField
              fullWidth
              variant="outlined"
              value={searchKey}
              onChange={event => setSearchKey(event.target.value)}
              placeholder="Search customers, sites, contacts, phone numbers, email, accounts or job PO..."
              autoFocus
              InputProps={{
                startAdornment: (
                  <SearchIcon className={classes.searchFieldIcon} />
                )
              }}
            />

            <Button
              type="submit"
              className={classes.searchButton}
            >
              Search
            </Button>

          </form>

          {message && (
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.message}
            >
              {message}
            </Typography>
          )}

        </Paper>

      {!isLoading && hasSearched && getTotalResults() > 0 && (
        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          {results.customers.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                document.getElementById("customer-results")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Customers ({results.customers.length})
            </Button>
          )}

          {results.jobs.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                document.getElementById("job-results")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Jobs ({results.jobs.length})
            </Button>
          )}

          {results.sites.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                document.getElementById("site-results")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Sites ({results.sites.length})
            </Button>
          )}

          {results.accounts.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                document.getElementById("account-results")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Accounts ({results.accounts.length})
            </Button>
          )}
        </Box>
      )}

      </div>

      <div className={classes.fixedSearchSpacer} />


      {isLoading && (
        <div className={classes.loading}>
          <CircularProgress size={32} />
        </div>
      )}


      {!isLoading && hasSearched && getTotalResults() === 0 && (
        <Paper className={classes.noResults}>
          <Typography color="textSecondary">
            No results found for "{searchKey}".
          </Typography>
        </Paper>
      )}



      {!isLoading && results.customers.length > 0 && (
        <div id="customer-results" className={classes.section}>

          <div className={classes.sectionTitle}>
            <CustomerIcon />
            <Typography variant="h6">
              Customers ({results.customers.length})
            </Typography>
          </div>

          <Grid container spacing={2}>

            {results.customers.map(customer => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                key={`customer-${customer.id}`}
              >

                <Paper
                  className={`${classes.resultCard} ${
                    customer.active === false ? classes.inactiveCard : ""
                  }`}
                  onClick={() => goToCustomer(customer.id)}
                >

                  <div className={classes.cardHeader}>

                    <div>
                      <Typography
                        variant="h6"
                        className={classes.resultName}
                      >
                        {highlightText(customer.name)}
                      </Typography>

                      {customer.company &&
                       customer.company !== customer.name && (
                        <Typography color="textSecondary">
                          {highlightText(customer.company)}
                        </Typography>
                      )}
                    </div>

                    {renderInactiveChip(customer)}

                  </div>


                  {customer.address && (
                    <Typography
                      variant="body2"
                      className={classes.detailLine}
                    >
                      {highlightText(customer.address)}
                    </Typography>
                  )}


                  {customer.contact && (
                    <Typography
                      variant="body2"
                      className={classes.detailLine}
                    >
                      Contact: {highlightContact(customer.contact)}
                    </Typography>
                  )}


                  {(customer.phone || customer.mobile) && (
                    <div className={classes.iconLine}>
                      <PhoneIcon className={classes.smallIcon} />

                      <Typography variant="body2">
                        {customer.phone && highlightPhone(customer.phone)}
                        {customer.phone && customer.mobile && " · "}
                        {customer.mobile && highlightPhone(customer.mobile)}
                      </Typography>
                    </div>
                  )}


                  {customer.email && (
                    <div className={classes.iconLine}>
                      <EmailIcon className={classes.smallIcon} />

                      <Typography variant="body2">
                        {highlightText(customer.email)}
                      </Typography>
                    </div>
                  )}


                  {renderMatchedText(customer)}

                </Paper>

              </Grid>
            ))}

          </Grid>

        </div>
      )}

      {!isLoading && results.jobs.length > 0 && (
        <div id="job-results" className={classes.section}>

          <div className={classes.sectionTitle}>
            <JobIcon />

            <div>
              <Typography variant="h6">
                Jobs ({results.jobs.length})
              </Typography>

              <Typography variant="caption" color="textSecondary">
                Job search currently matches PO / Order No. only.
              </Typography>
            </div>
          </div>

          <Grid container spacing={2}>

            {results.jobs.map(job => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                key={`job-${job.id}`}
              >

                <Paper
                  className={classes.resultCard}
                  onClick={() => goToJob(job.id)}
                >

                  <div className={classes.cardHeader}>
                    <div>

                      <Typography
                        variant="h6"
                        className={classes.resultName}
                      >
                        Job #{job.id}
                      </Typography>

                      {job.code && (
                        <Typography variant="body2">
                          PO / Order No: <strong>{highlightText(job.code)}</strong>
                        </Typography>
                      )}

                    </div>
                  </div>

                  {job.customer_name && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.detailLine}
                    >
                      Customer:{" "}
                      <span
                        className={classes.customerLinkText}
                        onClick={event => {
                          event.stopPropagation();
                          goToCustomer(job.customer_id);
                        }}
                      >
                        {job.customer_name}
                      </span>
                    </Typography>
                  )}

                  {job.site_name && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={classes.detailLine}
                    >
                      Site:{" "}
                      <span
                        className={classes.customerLinkText}
                        onClick={event => {
                          event.stopPropagation();
                          goToSite(job.site_id);
                        }}
                      >
                        {job.site_name}
                      </span>
                    </Typography>
                  )}

                </Paper>

              </Grid>
            ))}

          </Grid>

        </div>
      )}


      {!isLoading && results.sites.length > 0 && (
        <div id="site-results" className={classes.section}>

          <div className={classes.sectionTitle}>
            <SiteIcon />
            <Typography variant="h6">
              Sites ({results.sites.length})
            </Typography>
          </div>

          <Grid container spacing={2}>

            {results.sites.map(site => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                key={`site-${site.id}`}
              >

                <Paper
                  className={`${classes.resultCard} ${
                    site.active === false ? classes.inactiveCard : ""
                  }`}
                  onClick={() => goToSite(site.id)}
                >

                  <div className={classes.cardHeader}>

                    <div>
                      <Typography
                        variant="h6"
                        className={classes.resultName}
                      >
                        {highlightText(site.name)}
                      </Typography>

                      {site.customer_name && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className={classes.customerName}
                        >
                          Customer:{" "}
                          <span
                            className={classes.customerLinkText}
                            onClick={event => {
                              event.stopPropagation();
                              window.open(`#/app/customer/edit/${site.customer_id}`, "_blank");
                            }}
                          >
                            {highlightText(site.customer_name)}
                          </span>
                        </Typography>
                      )}
                    </div>

                    {renderInactiveChip(site)}

                  </div>


                  {site.code && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                    >
                      Site Code: {highlightText(site.code)}
                    </Typography>
                  )}


                  {site.address && (
                    <Typography
                      variant="body2"
                      className={classes.detailLine}
                    >
                      {highlightText(site.address)}
                    </Typography>
                  )}


                  {site.contact && (
                    <Typography
                      variant="body2"
                      className={classes.detailLine}
                    >
                      Contact: {highlightContact(site.contact)}
                    </Typography>
                  )}


                  {site.phone && (
                    <div className={classes.iconLine}>
                      <PhoneIcon className={classes.smallIcon} />

                      <Typography variant="body2">
                        {highlightPhone(site.phone)}
                      </Typography>
                    </div>
                  )}


                  {site.email && (
                    <div className={classes.iconLine}>
                      <EmailIcon className={classes.smallIcon} />

                      <Typography variant="body2">
                        {highlightText(site.email)}
                      </Typography>
                    </div>
                  )}


                  {renderMatchedText(site)}

                </Paper>

              </Grid>
            ))}

          </Grid>

        </div>
      )}


      {!isLoading && results.accounts.length > 0 && (
        <div id="account-results" className={classes.section}>

          <div className={classes.sectionTitle}>
            <AccountIcon />
            <Typography variant="h6">
              Accounts ({results.accounts.length})
            </Typography>
          </div>

          <Grid container spacing={2}>

            {results.accounts.map(account => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                key={`account-${account.id}`}
              >

                <Paper
                  className={`${classes.resultCard} ${
                    account.active === false ? classes.inactiveCard : ""
                  }`}
                  onClick={() => goToAccount(account.id)}
                >

                  <div className={classes.cardHeader}>

                    <div>

                      <Typography
                        variant="h6"
                        className={classes.resultName}
                      >
                        {highlightText(account.name)}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        Account Type: <strong>{account.account_type}</strong>
                      </Typography>

                      {account.account_type === "Administrator" && account.admin_access && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          Admin Access: <strong>{account.admin_access}</strong>
                        </Typography>
                      )}

                      {account.account_type === "Technician" && account.franchise_name && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          Region: {account.franchise_name}
                        </Typography>
                      )}

                      {account.account_type === "Customer" && account.customer_name && (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          Customer:{" "}
                          <span
                            className={classes.customerLinkText}
                            onClick={event => {
                              event.stopPropagation();
                              window.open(
                                `#/app/customer/edit/${account.customer_id}`,
                                "_blank"
                              );
                            }}
                          >
                            {highlightText(account.customer_name)}
                          </span>
                        </Typography>
                      )}

                    </div>

                    {renderInactiveChip(account)}

                  </div>


                  {account.account_type === "CUSTOMER_PORTAL" &&
                   account.customer_name && (
                    <Box mt={1} mb={1}>

                      <Button
                        size="small"
                        color="primary"
                        className={classes.customerLink}
                        onClick={event => {
                          event.stopPropagation();
                          goToCustomer(account.customer_id);
                        }}
                      >
                        {highlightText(account.customer_name)}
                      </Button>

                    </Box>
                  )}


                  {account.signon && (
                    <Typography
                      variant="body2"
                      className={classes.detailLine}
                    >
                      Signon: {highlightText(account.signon)}
                    </Typography>
                  )}


                  {account.phone && (
                    <div className={classes.iconLine}>
                      <PhoneIcon className={classes.smallIcon} />

                      <Typography variant="body2">
                        {highlightPhone(account.phone)}
                      </Typography>
                    </div>
                  )}


                  {account.email && (
                    <div className={classes.iconLine}>
                      <EmailIcon className={classes.smallIcon} />

                      <Typography variant="body2">
                        {highlightText(account.email)}
                      </Typography>
                    </div>
                  )}

                </Paper>

              </Grid>
            ))}

          </Grid>

        </div>
      )}

    </>
  );
}
